import useOverlay from "@/hooks/common/useOverlay";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";
import * as style from "@COMPONENTS/style/productlist/productlistM";
import {
	FilterButton,
	FilterLeftBox,
	FilterRightBox,
	FilterTag,
	FilterTagWrap,
	FilterTop,
	SelectBox,
	TagContainer,
} from "@FRAMEWORK/ui/mobile/filter";
import {Checkbox} from "@FRAMEWORK/ui/mobile/input";
import React, {forwardRef, useEffect, useState} from "react";
import useFilterStore from "../../common/filterHook";
import LocalDetailFilter from "./detailFilter";

const LocalLvl4ListComponent = ({listStr = ""}: {listStr: string}) => {
	const {getFilter, addFilter, removeFilter} = useFilterStore();
	const filters = getFilter();
	const lvl4List: {code: string; name: string}[] = [];
	const lvl4ListData = listStr.replaceAll("|", "").split("^");
	lvl4ListData.forEach((item, idx) => {
		if (idx % 2 === 0) {
			const code = item;
			const name = lvl4ListData[idx + 1];
			lvl4List.push({code, name});
		}
	});
	const handleClick = (e: React.ChangeEvent<HTMLInputElement>) => {
		const {value, checked} = e.target;
		const text = e.target.parentNode.textContent;
		checked && addFilter("dspSid", value, text);
		!checked && removeFilter("dspSid", value);
	};

	if (!filters) return <></>;

	return (
		<style.AreaGridList>
			{lvl4List.map((item, index) => (
				<li key={index}>
					<Checkbox
						id={`dspSid${index}`}
						onChange={handleClick}
						checked={filters.some(v => v.value === item.code)}
						value={`${item.code}`}
					/>
					<label htmlFor={`dspSid${index}`}>{item.name}</label>
				</li>
			))}
		</style.AreaGridList>
	);
};

const LocalLvl4List = ({listStr}: {listStr: string}) => (
	<ErrorBoundary logMsg="HeadFilter Lvl4List Error">
		<LocalLvl4ListComponent listStr={listStr} />
	</ErrorBoundary>
);

const LocalFilterBarComponent = forwardRef<HTMLDivElement>((props, ref) => {
	const {getFilteringProduct, getFilter, setFilter} = useFilterStore();
	const productList = getFilteringProduct();
	const productCount = productList.length || 0; // 상품 갯수
	const key = "sort";

	const handleSelect = (e: React.ChangeEvent<HTMLSelectElement>) => {
		const {dataset} = e.target.selectedOptions[0];
		const setArr = getFilter()?.filter(v => v.key !== key);
		dataset && setArr.push({key, value: dataset.value, text: dataset.text, asc: dataset.asc === "true"});
		setFilter(setArr);
	};

	const [open] = useOverlay();
	const openFilter = () => {
		open(overlayProps => <LocalDetailFilter {...overlayProps} />);
	};

	return (
		<style.FixedHeader ref={ref}>
			<FilterTop>
				<FilterLeftBox>
					상품 <em>{productCount}</em>개
				</FilterLeftBox>
				<FilterRightBox>
					<SelectBox>
						<select onChange={handleSelect}>
							<option value="sortOrder" data-asc={true} data-value="sortOrder" data-text="추천순">
								추천순
							</option>
							<option value="rervTourlistCnt" data-asc={false} data-value="rervTourlistCnt" data-text="인기순">
								인기순
							</option>
							<option value="minPrice" data-asc={true} data-value="minPrice" data-text="최저가순">
								최저가순
							</option>
							<option value="maxPrice" data-asc={false} data-value="minPrice" data-text="최고가순">
								최고가순
							</option>
							<option value="rvwAvgScore" data-asc={false} data-value="rvwAvgScore" data-text="평점순">
								평점순
							</option>
						</select>
					</SelectBox>
					<FilterButton type="button" onClick={openFilter}>
						필터
					</FilterButton>
				</FilterRightBox>
			</FilterTop>
		</style.FixedHeader>
	);
});

const LocalFilterBar = forwardRef<HTMLDivElement>((props, ref) => (
	<ErrorBoundary logMsg="HeadFilter FilterBar Error">
		<LocalFilterBarComponent {...props} ref={ref} />
	</ErrorBoundary>
));

const SUMMARY_FILTER_KEYS = [
	"outStartDateList" /* 출발 일정 월, 날짜 */,
	"wdayList" /* 출발 일정 요일*/,
	"outDeprtFgList" /* 출발 시간 */,
	"outCityDiviNm" /* 출발지(공항) */,
	"evDayCntList" /* 여행기간 */,
	"minPrice" /* 최소금액 */,
	"maxPrice" /* 최대금액 */,
	"airCd" /* 이용교통 */,
	"goodsFeaturesList" /* 상품특징 */,
	"goodsPromotion" /* 프로모션 */,
	"goodsTmember" /* 여행구성원 */,
	"goodsNm" /* 상품 상세 검색 */,
];

const LocalFilterSummaryComponent = ({isScroll}: {isScroll: boolean}) => {
	const {getFilter, removeFilter, setFilter} = useFilterStore();
	const filters = getFilter();
	// filter 필드 병합 및 일부 필드 노출용 상태 관리
	const [summaryFilters, setSummaryFilters] = useState<{key: string; value: string; text: string}[]>([]);

	useEffect(() => {
		if (!filters) return;

		setSummaryFilters([]);
		// 전체 필터 중 일부 필터만 보여주기 위한 초기 데이터 설정
		SUMMARY_FILTER_KEYS.forEach(filterName => {
			// minPrice와 maxPrice는 전부 있거나 전부 없음
			if (filterName === "maxPrice") return;
			if (filterName === "minPrice") {
				const minPrice = filters.find(e => e.key === "minPrice");
				const maxPrice = filters.find(e => e.key === "maxPrice");

				if (!minPrice || !maxPrice) return;

				return setSummaryFilters(prev => [
					...prev,
					{
						key: "minMaxPrice",
						value: `₩${minPrice.text} ~ ₩${maxPrice.text}`,
						text: `₩${minPrice.text} ~ ₩${maxPrice.text}`,
					},
				]);
			}
			setSummaryFilters(prev => [...prev, ...filters.filter(e => e.key === filterName)]);
		});
	}, [filters]);

	const removeAllFilters = () => {
		setFilter(getFilter().filter(item => item.key === "sort"));
		setSummaryFilters([]);
	};

	const removeThisFilter = (filter: {key: string; value: string; text: string}) => {
		if (filter.key === "minMaxPrice") {
			const setArr = filters.filter(e => e.key !== "minPrice" && e.key !== "maxPrice");
			setSummaryFilters(summaryFilters.filter(e => e.key !== "minMaxPrice"));
			return setFilter(setArr);
		}
		setSummaryFilters(summaryFilters.filter(e => e.key !== filter.key));
		removeFilter(filter.key, filter.value);
	};

	return (
		<style.FilterTagHeader isScroll={isScroll}>
			{!!summaryFilters.length && (
				<TagContainer>
					<style.ResetButton type="button" onClick={removeAllFilters}>
						전체삭제
					</style.ResetButton>
					<FilterTagWrap>
						{summaryFilters.map((filtr, idx) => (
							<FilterTag key={idx}>
								{filtr.text}
								<i onClick={() => removeThisFilter(filtr)}>닫기</i>
							</FilterTag>
						))}
					</FilterTagWrap>
				</TagContainer>
			)}
		</style.FilterTagHeader>
	);
};

const LocalFilterSummary = ({isScroll}: {isScroll: boolean}) => (
	<ErrorBoundary logMsg="HeadFilter FilterSummary Error ">
		<LocalFilterSummaryComponent isScroll={isScroll} />
	</ErrorBoundary>
);
export {LocalFilterBar, LocalFilterSummary, LocalLvl4List};
